import React from "react";

/**
 * MatchVideoEmbed — renders an inline embedded player for YouTube,
 * Streamable or Twitch clip URLs. Matches the whitelist enforced by
 * `services.video_urls.normalise_video_url` on the backend.
 *
 * Props:
 *   url: string — already-validated URL from `team_challenges.video_url`
 *                 or `score_events.video_url`.
 *   compact?: bool — renders a thumbnail link (no iframe) when true
 */
function classify(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const h = u.hostname.toLowerCase();
    if (h.endsWith("youtu.be")) {
      const id = (u.pathname || "/").split("/").filter(Boolean)[0];
      return { kind: "youtube", embedUrl: `https://www.youtube.com/embed/${id}`, host: "YouTube" };
    }
    if (h.endsWith("youtube.com")) {
      if ((u.pathname || "").startsWith("/embed/")) {
        return { kind: "youtube", embedUrl: url, host: "YouTube" };
      }
      if ((u.pathname || "").startsWith("/shorts/")) {
        const id = u.pathname.split("/")[2];
        return { kind: "youtube", embedUrl: `https://www.youtube.com/embed/${id}`, host: "YouTube" };
      }
      const id = u.searchParams.get("v");
      if (id) return { kind: "youtube", embedUrl: `https://www.youtube.com/embed/${id}`, host: "YouTube" };
    }
    if (h.endsWith("streamable.com")) {
      const id = (u.pathname || "/").split("/").filter(Boolean)[0];
      return { kind: "streamable", embedUrl: `https://streamable.com/e/${id}`, host: "Streamable" };
    }
    if (h === "clips.twitch.tv") {
      const slug = (u.pathname || "/").split("/").filter(Boolean)[0];
      const parent = (typeof window !== "undefined" ? window.location.hostname : "leaguehub.co");
      return {
        kind: "twitch",
        embedUrl: `https://clips.twitch.tv/embed?clip=${slug}&parent=${parent}`,
        host: "Twitch",
      };
    }
    if (h === "twitch.tv" || h === "www.twitch.tv") {
      // accept .../clip/<slug> form
      const parts = (u.pathname || "").split("/").filter(Boolean);
      const idx = parts.indexOf("clip");
      if (idx >= 0 && parts[idx + 1]) {
        const slug = parts[idx + 1];
        const parent = (typeof window !== "undefined" ? window.location.hostname : "leaguehub.co");
        return {
          kind: "twitch",
          embedUrl: `https://clips.twitch.tv/embed?clip=${slug}&parent=${parent}`,
          host: "Twitch",
        };
      }
    }
  } catch (_e) { /* fall through */ }
  return null;
}

export function MatchVideoEmbed({ url, compact }) {
  const info = classify(url);
  if (!info) {
    if (!url) return null;
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        data-testid="match-video-link"
        className="text-[11px] text-[#14b8a6] hover:underline inline-flex items-center gap-1"
      >
        <i className="fas fa-up-right-from-square" />Open clip
      </a>
    );
  }
  if (compact) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        data-testid={`match-video-thumb-${info.kind}`}
        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-black/40 border border-white/15 hover:border-[#14b8a6]/60 text-white"
      >
        <i className={`fab ${info.kind === "youtube" ? "fa-youtube text-[#ff0033]" : info.kind === "twitch" ? "fa-twitch text-[#9146ff]" : "fa-circle-play text-[#14b8a6]"}`} />
        {info.host}
      </a>
    );
  }
  return (
    <div
      data-testid={`match-video-embed-${info.kind}`}
      className="rounded-2xl overflow-hidden border border-white/10 bg-black"
      style={{ aspectRatio: "16 / 9" }}
    >
      <iframe
        title="Match clip"
        src={info.embedUrl}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        loading="lazy"
        className="w-full h-full"
      />
    </div>
  );
}

export default MatchVideoEmbed;
